import React, { ReactNode } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { CustomTooltip } from 'src/content/keywordResearch/CustomTooltip';
import { keywordResearchDashBoardCss } from 'src/content/keywordResearch/keywordResearchCSS';

// ─── Root Component Props ────────────────────────────────────────────────────

/**
 * Props for the {@link ScoreCircularProgressV2} root component.
 *
 * @property value - Score value (0–100) that controls the progress arc fill.
 * @property size - Circle diameter in pixels. Defaults to `170`.
 * @property thickness - Stroke thickness of the progress arc. Defaults to `5`.
 * @property progressColor - Override the foreground stroke color. When omitted the color is auto-derived from `value` using score-based thresholds (red → orange → yellow → chartreuse → green).
 * @property loading - When `true`, renders a centered spinner instead of the circle.
 * @property tooltipText - Optional tooltip content shown on hover over the center area. Accepts a string or ReactNode. When omitted, no tooltip is rendered.
 * @property className - Additional Tailwind classes applied to the outermost wrapper.
 * @property children - Composable content rendered inside the circle (use compound sub-components).
 */
interface ScoreCircularProgressV2Props {
  value: number;
  size?: number;
  thickness?: number;
  progressColor?: string;
  showGradient?: boolean;
  gradientColors?: {
    start: string;
    end: string;
  };
  gradientId?: string;
  loading?: boolean;
  tooltipText?: ReactNode;
  className?: string;
  children?: React.ReactNode;
}

// ─── Sub-component Props ─────────────────────────────────────────────────────

/** Props for {@link ScoreCircularProgressV2.Value}. */
interface ValueProps {
  /** Numeric score to display (e.g. `78` renders as `78/100`). */
  value: number;
  /** Additional Tailwind classes. */
  className?: string;
  scoreClass?: string;
}

/** Props for {@link ScoreCircularProgressV2.Trend}. */
interface TrendProps {
  /** Trend delta value. Positive = up, negative = down, `0` = no change, `null`/`undefined` = show `modeText` instead. */
  trendValue?: number | null;
  /** Fallback text shown when `trendValue` is `null` or `undefined` (e.g. `"Weekly"`, `"Monthly"`). */
  modeText?: string;
  /** Text displayed when `trendValue === 0`. Defaults to `"No Changes"`. */
  noChangeText?: string;
  /** Whether to append % to the trend value. Defaults to `false`. */
  showPercent?: boolean;
  /** Additional Tailwind classes. */
  className?: string;
}

/** Props for {@link ScoreCircularProgressV2.Title}. */
interface TitleProps {
  /** Title text displayed above the score value. */
  title: string;
  /** Additional Tailwind classes. */
  className?: string;
}

// ─── Title Sub-component ─────────────────────────────────────────────────────

/**
 * Renders a centered title label inside the circular progress.
 */
const Title: React.FC<TitleProps> = ({ title, className = '' }) => {
  return (
    <p
      className={`font-inter text-sm font-medium text-center text-primary ${className}`}
    >
      {title}
    </p>
  );
};

// ─── Value Sub-component ─────────────────────────────────────────────────────

/**
 * Renders the score value in `{value}/100` format.
 */
const Value: React.FC<ValueProps> = ({ value, className = '', scoreClass = "" }) => {
  return (
    <p
      className={`font-inter text-lg font-medium text-center text-[#686685] ${className}`}
    >
      <span className={`text-[32px] font-bold text-primary ${scoreClass}`}>
        {value}
      </span>
      /100
    </p>
  );
};

// ─── Trend Sub-component ─────────────────────────────────────────────────────

const Trend: React.FC<TrendProps> = ({
  trendValue,
  modeText,
  noChangeText = 'No Changes',
  showPercent = false,
  className = ''
}) => {
  // No trend data available — show mode text
  if (trendValue === null || trendValue === undefined) {
    return (
      <p
        className={`text-sm font-semibold text-primary ${className}`}
      >
        {modeText}
      </p>
    );
  }

  const numericTrend = parseFloat(String(trendValue));

  // No change
  if (numericTrend === 0) {
    return (
      <p
        className={`font-inter text-sm font-semibold text-[#667085] ${className}`}
      >
        {noChangeText}
      </p>
    );
  }

  // Trending down
  if (numericTrend < 0) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <TrendingDownIcon sx={{ color: 'var(--color-error-bright, #D92D20)', fontSize: 18 }} />
        <span className="font-inter text-sm font-semibold text-[#D92D20]">
          {`${numericTrend}${showPercent ? '%' : ''}`}
        </span>
      </div>
    );
  }

  // Trending up
  const formattedVal = numericTrend > 0 ? `+${numericTrend}` : `${numericTrend}`;
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <img src="/static/images/icons/green_up.svg" alt="up" className="w-4 h-4" />
      <span className="font-inter text-sm font-semibold text-[#12B76A]">
        {`${formattedVal}${showPercent ? '%' : ''}`}
      </span>
    </div>
  );
};

// ─── Color & Gradient helpers ────────────────────────────────────────────────

/**
 * Returns dynamic gradient start and end colors matching AI Visibility score ranges & colors:
 * - value <= 30: #D33F25 -> #663FDA
 * - 30 < value <= 50: #FF8A00 -> #FFB347
 * - 50 < value <= 70: #E5D135 -> #CF804F
 * - 70 < value <= 90: #25D3B1 -> #6EE7B7
 * - value > 90: #25D3B1 -> #6544C8
 */
export const getGradientByValue = (value: number): { start: string; end: string } => {
  if (typeof value !== 'number' || isNaN(value)) {
    return { start: '#D33F25', end: '#663FDA' };
  }
  if (value <= 30) {
    return { start: '#D33F25', end: '#FF8A00' };
  }
  if (value <= 50) {
    return { start: '#FF8A00', end: '#FFB347' };
  }
  if (value <= 70) {
    return { start: '#E5D135', end: '#CF804F' };
  }
  if (value <= 90) {
    return { start: '#25D3B1', end: '#6EE7B7' };
  }
  return { start: '#25D3B1', end: '#6544C8' };
};

export const getColorByValue = (value: number): string => {
  if (value <= 30) return '#D33F25';
  if (value <= 50) return '#FF8A00';
  if (value <= 70) return '#E5D135';
  return '#25D3B1';
};

export const getLinearGradientByScore = (value: number): string => {
  const { start, end } = getGradientByValue(value);
  return `linear-gradient(90deg, ${start} 0%, ${end} 100%)`;
};

// ─── Root Component ──────────────────────────────────────────────────────────

const ScoreCircularProgressV2: React.FC<ScoreCircularProgressV2Props> & {
  Title: typeof Title;
  Value: typeof Value;
  Trend: typeof Trend;
} = ({
  value,
  size = 170,
  thickness = 5,
  progressColor,
  showGradient = true,
  gradientColors,
  gradientId,
  loading = false,
  tooltipText,
  className = '',
  children
}) => {
  const generatedId = React.useId ? React.useId().replace(/:/g, '') : 'score-grad';
  const activeGradientId = gradientId || `score-gradient-${generatedId}`;

  const activeGradient = gradientColors || getGradientByValue(value);
  const resolvedColor = progressColor ?? getColorByValue(value);

  if (loading) {
    return (
      <div
        className={`flex justify-center items-center mt-6 ${className}`}
        style={{ height: size }}
      >
        <CircularProgress
          size={44}
          color="inherit"
          sx={{ color: 'var(--color-primary)' }}
        />
      </div>
    );
  }

  const useGrad = showGradient && Boolean(activeGradient);

  return (
    <div
      className={`relative flex justify-center items-center mx-auto ${className}`}
      style={{ width: size, height: size }}
    >
      {/* SVG Gradient Definition */}
      {useGrad && activeGradient && (
        <svg width="0" height="0" style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            <linearGradient id={activeGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={activeGradient.start} />
              <stop offset="100%" stopColor={activeGradient.end} />
            </linearGradient>
          </defs>
        </svg>
      )}

      {/* Background Circle (MUI) */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{
          position: 'absolute',
          color: '#F2F4F7',
          top: 0,
          left: 0
        }}
      />

      {/* Foreground Circle (MUI) */}
      <CircularProgress
        variant="determinate"
        value={value}
        size={size}
        thickness={thickness}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          color: useGrad ? undefined : resolvedColor,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
            ...(useGrad ? { stroke: `url(#${activeGradientId})` } : {})
          }
        }}
      />

      {/* Composable content area (centered inside the circle) */}
      {tooltipText ? (
        <CustomTooltip
          title={
            <Typography sx={keywordResearchDashBoardCss?.customTollTipText}>
              {tooltipText}
            </Typography>
          }
          placement="bottom"
        >
          <div className="absolute flex flex-col items-center cursor-default">
            {children}
          </div>
        </CustomTooltip>
      ) : (
        <div className="absolute flex flex-col items-center">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Attach sub-components ───────────────────────────────────────────────────

ScoreCircularProgressV2.Title = Title;
ScoreCircularProgressV2.Value = Value;
ScoreCircularProgressV2.Trend = Trend;

export default ScoreCircularProgressV2;
